/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — emits its standard event on document. This bridge
 *     waits for the event promise, refreshes the canonical Dawn/DD cart
 *     surfaces, and publishes `cart-update` once the server mutation is
 *     complete.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

const STANDARD_CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
const STANDARD_ACTION_CONTEXT = 'standard-action';
const STANDARD_ACTIONS_RETRY_DELAY = 250;
const STANDARD_ACTIONS_MAX_ATTEMPTS = 40;
const handledStandardActionPromises = new WeakSet();

let standardActionsRetryTimer;
let standardActionsAttempts = 0;
let openCartConfigurationAttempted = false;
let updateCartConfigurationAttempted = false;

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI(focus) {
  const drawer = document.querySelector('cart-drawer');
  if (typeof drawer?.refreshContents === 'function') {
    const renderOptions = { shouldOpen: false };
    if (focus) renderOptions.focus = focus;

    const cartData = await drawer.refreshContents(renderOptions);
    if (typeof drawer.publishCartUpdate === 'function') {
      await drawer.publishCartUpdate(cartData, STANDARD_ACTION_CONTEXT);
    } else {
      await Promise.resolve(
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: STANDARD_ACTION_CONTEXT,
          cartData,
          sectionsRendered: true,
        })
      ).catch((error) => console.error(error));
    }
    return cartData;
  }

  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: STANDARD_ACTION_CONTEXT,
    cartData: cartData ?? undefined,
  });

  return cartData;
}

function captureDrawerFocus() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer?.classList?.contains('active')) return undefined;

  const activeElement = document.activeElement;
  if (!activeElement || !drawer.contains(activeElement)) {
    return { context: 'drawer', selector: '.drawer__close' };
  }

  const quantityInput = activeElement.matches?.('[data-quantity-line-key], [data-quantity-variant-id]')
    ? activeElement
    : activeElement.closest?.('quantity-input')?.querySelector(
        '[data-quantity-line-key], [data-quantity-variant-id]'
      );
  if (quantityInput) {
    return {
      context: 'drawer',
      lineKey: quantityInput.dataset.quantityLineKey,
      variantId: quantityInput.dataset.quantityVariantId,
      controlName: activeElement.getAttribute?.('name'),
    };
  }

  if (activeElement.matches?.('.drawer__close')) {
    return { context: 'drawer', selector: '.drawer__close' };
  }
  if (activeElement.matches?.('[data-dd-mini-cart-remove-discount]')) {
    return { context: 'drawer', selector: '[data-dd-mini-cart-remove-discount]' };
  }
  if (activeElement.matches?.('[data-dd-mini-cart-discount-form] [name="discount"]')) {
    return {
      context: 'drawer',
      selector: '[data-dd-mini-cart-discount-form] [name="discount"]',
      select: true,
    };
  }

  return { context: 'drawer', selector: '.drawer__close' };
}

async function handleStandardCartLinesUpdate(event) {
  if (event.context !== STANDARD_ACTION_CONTEXT) return;

  const actionPromise = event.promise;
  const canTrackPromise =
    actionPromise && (typeof actionPromise === 'object' || typeof actionPromise === 'function');
  if (!canTrackPromise || typeof actionPromise.then !== 'function') return;
  if (handledStandardActionPromises.has(actionPromise)) return;
  handledStandardActionPromises.add(actionPromise);

  const focus = captureDrawerFocus();

  try {
    await actionPromise;
    await refreshDawnCartUI(focus);
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.error('[Dawn] Standard Action cart refresh failed.', error);
    }
  }
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return false;

  if (!openCartConfigurationAttempted && typeof actions.openCart?.configure === 'function') {
    openCartConfigurationAttempted = true;
    actions.openCart.configure({
      async handler(defaultHandler) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
          return;
        }
        return defaultHandler();
      },
    });
  }

  if (!updateCartConfigurationAttempted && typeof actions.updateCart?.configure === 'function') {
    updateCartConfigurationAttempted = true;
    actions.updateCart.configure({
      eventTarget: () => document,
      async handler(defaultHandler) {
        return defaultHandler();
      },
    });
  }

  return openCartConfigurationAttempted && updateCartConfigurationAttempted;
}

function scheduleStandardActionsConfiguration() {
  if (initStandardActions() || standardActionsRetryTimer) return;
  if (standardActionsAttempts >= STANDARD_ACTIONS_MAX_ATTEMPTS) return;

  standardActionsRetryTimer = window.setTimeout(() => {
    standardActionsRetryTimer = undefined;
    standardActionsAttempts += 1;
    scheduleStandardActionsConfiguration();
  }, STANDARD_ACTIONS_RETRY_DELAY);
}

// Standard cart events remain the reliable integration contract even when
// Shopify.actions is attached after this deferred script or isn't exposed as
// a window global by the active Storefront Renderer. Filtering by context
// keeps Dawn's ordinary product/cart events on their existing render path.
document.addEventListener(STANDARD_CART_LINES_UPDATE_EVENT, handleStandardCartLinesUpdate);

scheduleStandardActionsConfiguration();
document.addEventListener('DOMContentLoaded', scheduleStandardActionsConfiguration, { once: true });
window.addEventListener?.('load', scheduleStandardActionsConfiguration, { once: true });
